import ApiClient from './apiClient';
import type { 
  EmpresaListItem,
  CreateEmpresaRequest,
  UpdateEmpresaRequest,
  UsuarioEmpresa,
  UsuarioAuthService,
  AsignarUsuarioRequest,
  UpdateUsuarioEmpresaRequest,
  AsignarMasivoRequest,
  BuscarUsuariosResponse,
  EmpresasFiltros,
  UsuariosFiltros,
  EstadisticasEmpresaAdmin,
  PaginatedResponse,
  CreateUpdateResponse,
  DeleteResponse
} from '../types';

// ===============================
// SERVICIO ADMINISTRACIÓN DE EMPRESAS
// ===============================

class EmpresasAdminService {
  private basePath = '/admin/empresas';
  private usuariosPath = '/admin/usuarios';

  // ===============================
  // GESTIÓN DE EMPRESAS
  // ===============================

  /**
   * 📋 Obtener lista de empresas con filtros y paginación
   */
  async obtenerEmpresas(filtros: EmpresasFiltros = {}): Promise<PaginatedResponse<EmpresaListItem>> {
    try {
      const params = new URLSearchParams();
      
      if (filtros.busqueda) params.append('busqueda', filtros.busqueda);
      if (filtros.es_activa !== undefined) params.append('es_activa', filtros.es_activa.toString());
      if (filtros.page) params.append('page', filtros.page.toString());
      if (filtros.per_page) params.append('per_page', filtros.per_page.toString());
      if (filtros.orden) params.append('orden', filtros.orden);
      if (filtros.direccion) params.append('direccion', filtros.direccion);

      const response = await ApiClient.get<PaginatedResponse<EmpresaListItem>>(
        `${this.basePath}?${params.toString()}`
      );
      
      return response.data!;
    } catch (error: any) {
      console.error('Error al obtener empresas:', error);
      throw error;
    }
  }

  /**
   * 👁️ Obtener detalles de una empresa específica
   */
  async obtenerEmpresa(empresaId: number): Promise<EmpresaListItem> {
    try {
      const response = await ApiClient.get<EmpresaListItem>(`${this.basePath}/${empresaId}`);
      return response.data!;
    } catch (error: any) {
      console.error(`Error al obtener empresa ${empresaId}:`, error);
      throw error;
    }
  }

  /**
   * ➕ Crear nueva empresa
   */
  async crearEmpresa(datos: CreateEmpresaRequest): Promise<CreateUpdateResponse<EmpresaListItem>> {
    try {
      const response = await ApiClient.post<CreateUpdateResponse<EmpresaListItem>>(
        this.basePath,
        datos
      );
      return response.data!;
    } catch (error: any) {
      console.error('Error al crear empresa:', error);
      throw error;
    }
  }

  /**
   * ✏️ Actualizar empresa existente
   */
  async actualizarEmpresa(
    empresaId: number, 
    datos: UpdateEmpresaRequest
  ): Promise<CreateUpdateResponse<EmpresaListItem>> {
    try {
      const response = await ApiClient.put<CreateUpdateResponse<EmpresaListItem>>(
        `${this.basePath}/${empresaId}`,
        datos
      );
      return response.data!;
    } catch (error: any) {
      console.error(`Error al actualizar empresa ${empresaId}:`, error);
      throw error;
    }
  }

  /**
   * 🗑️ Desactivar empresa (soft delete)
   */
  async desactivarEmpresa(empresaId: number): Promise<DeleteResponse> {
    try {
      const response = await ApiClient.delete<DeleteResponse>(`${this.basePath}/${empresaId}`);
      return response.data!;
    } catch (error: any) {
      console.error(`Error al desactivar empresa ${empresaId}:`, error);
      throw error;
    }
  }

  /**
   * 📊 Obtener estadísticas de una empresa
   */
  async obtenerEstadisticasEmpresa(empresaId: number): Promise<EstadisticasEmpresaAdmin> {
    try {
      const response = await ApiClient.get<EstadisticasEmpresaAdmin>(
        `${this.basePath}/${empresaId}/estadisticas`
      );
      return response.data!;
    } catch (error: any) {
      console.error(`Error al obtener estadísticas de empresa ${empresaId}:`, error);
      throw error;
    }
  }

  // ===============================
  // GESTIÓN DE USUARIOS POR EMPRESA
  // ===============================

  /**
   * 👥 Obtener usuarios asignados a una empresa
   */
  async obtenerUsuariosEmpresa(empresaId: number): Promise<UsuarioEmpresa[]> {
    try {
      const response = await ApiClient.get<UsuarioEmpresa[]>(`${this.basePath}/${empresaId}/usuarios`);
      return response.data!;
    } catch (error: any) {
      console.error(`Error al obtener usuarios de empresa ${empresaId}:`, error);
      throw error;
    }
  }

  /**
   * ➕ Asignar usuario a empresa
   */
  async asignarUsuario(
    empresaId: number, 
    datos: AsignarUsuarioRequest
  ): Promise<CreateUpdateResponse<UsuarioEmpresa>> {
    try {
      const response = await ApiClient.post<CreateUpdateResponse<UsuarioEmpresa>>(
        `${this.basePath}/${empresaId}/usuarios`,
        datos
      );
      return response.data!;
    } catch (error: any) {
      console.error(`Error al asignar usuario a empresa ${empresaId}:`, error);
      throw error;
    }
  }

  /**
   * ✏️ Actualizar permisos de usuario en empresa
   */
  async actualizarUsuarioEmpresa(
    empresaId: number,
    usuarioId: number,
    datos: UpdateUsuarioEmpresaRequest
  ): Promise<CreateUpdateResponse<UsuarioEmpresa>> {
    try {
      const response = await ApiClient.put<CreateUpdateResponse<UsuarioEmpresa>>(
        `${this.basePath}/${empresaId}/usuarios/${usuarioId}`,
        datos
      );
      return response.data!;
    } catch (error: any) {
      console.error(`Error al actualizar usuario ${usuarioId} en empresa ${empresaId}:`, error);
      throw error;
    }
  }

  /**
   * 🗑️ Desasignar usuario de empresa
   */
  async desasignarUsuario(empresaId: number, usuarioId: number): Promise<DeleteResponse> {
    try {
      const response = await ApiClient.delete<DeleteResponse>(
        `${this.basePath}/${empresaId}/usuarios/${usuarioId}`
      );
      return response.data!;
    } catch (error: any) {
      console.error(`Error al desasignar usuario ${usuarioId} de empresa ${empresaId}:`, error);
      throw error;
    }
  }

  // ===============================
  // BÚSQUEDA Y GESTIÓN DE USUARIOS EXTERNOS
  // ===============================

  /**
   * 🔍 Buscar usuarios en el auth service
   */
  async buscarUsuarios(filtros: UsuariosFiltros = {}): Promise<BuscarUsuariosResponse> {
    try {
      const params = new URLSearchParams();
      
      if (filtros.busqueda) params.append('busqueda', filtros.busqueda);
      if (filtros.tiene_acceso_mava !== undefined) {
        params.append('tiene_acceso_mava', filtros.tiene_acceso_mava.toString());
      }
      if (filtros.sin_empresa !== undefined) {
        params.append('sin_empresa', filtros.sin_empresa.toString());
      }
      if (filtros.page) params.append('page', filtros.page.toString());
      if (filtros.per_page) params.append('per_page', filtros.per_page.toString());

      const response = await ApiClient.get<BuscarUsuariosResponse>(
        `${this.usuariosPath}/buscar?${params.toString()}`
      );
      
      return response.data!;
    } catch (error: any) {
      console.error('Error al buscar usuarios:', error);
      throw error;
    }
  }

  /**
   * 👥 Obtener usuarios disponibles (sin empresa asignada)
   */
  async obtenerUsuariosDisponibles(): Promise<UsuarioAuthService[]> {
    try {
      const response = await ApiClient.get<UsuarioAuthService[]>(`${this.usuariosPath}/disponibles`);
      return response.data!;
    } catch (error: any) {
      console.error('Error al obtener usuarios disponibles:', error);
      throw error;
    }
  }

  /**
   * 📤 Asignación masiva de usuarios
   */
  async asignarMasivo(datos: AsignarMasivoRequest): Promise<CreateUpdateResponse<any>> {
    try {
      const response = await ApiClient.post<CreateUpdateResponse<any>>(
        `${this.usuariosPath}/asignar-masivo`,
        datos
      );
      return response.data!;
    } catch (error: any) {
      console.error('Error en asignación masiva:', error);
      throw error;
    }
  }

  // ===============================
  // MÉTODOS AUXILIARES
  // ===============================

  /**
   * 📊 Obtener resumen de todas las empresas
   */
  async obtenerResumenEmpresas(): Promise<{
    total: number;
    activas: number;
    inactivas: number;
    total_usuarios: number;
    total_reportes: number;
  }> {
    try {
      const response = await ApiClient.get(`${this.basePath}/resumen`);
      return response.data!;
    } catch (error: any) {
      console.error('Error al obtener resumen de empresas:', error);
      throw error;
    }
  }

  /**
   * 📋 Exportar empresas a CSV
   */
  async exportarEmpresas(filtros: EmpresasFiltros = {}): Promise<Blob> {
    try {
      const params = new URLSearchParams();
      
      if (filtros.busqueda) params.append('busqueda', filtros.busqueda);
      if (filtros.es_activa !== undefined) params.append('es_activa', filtros.es_activa.toString());

      const response = await ApiClient.get(
        `${this.basePath}/export?${params.toString()}`,
        {
          responseType: 'blob'
        }
      );
      
      return response.data as Blob;
    } catch (error: any) {
      console.error('Error al exportar empresas:', error);
      throw error;
    }
  }

  /**
   * ✅ Validar RUT chileno
   */
  validarRut(rut: string): boolean {
    // Implementación básica de validación de RUT chileno
    const rutLimpio = rut.replace(/[^\dkK]/g, '');
    
    if (rutLimpio.length < 8 || rutLimpio.length > 9) {
      return false;
    }

    const rutNumeros = rutLimpio.slice(0, -1);
    const dv = rutLimpio.slice(-1).toUpperCase();
    
    let suma = 0;
    let multiplicador = 2;
    
    for (let i = rutNumeros.length - 1; i >= 0; i--) {
      suma += parseInt(rutNumeros[i]) * multiplicador;
      multiplicador = multiplicador === 7 ? 2 : multiplicador + 1;
    }
    
    const resto = suma % 11;
    const dvCalculado = resto < 2 ? resto.toString() : resto === 10 ? 'K' : (11 - resto).toString();
    
    return dv === dvCalculado;
  }

  /**
   * 📧 Validar formato de email
   */
  validarEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * 📱 Validar formato de teléfono chileno
   */
  validarTelefono(telefono: string): boolean {
    const telefonoRegex = /^\+56[1-9]\d{8}$/;
    return telefonoRegex.test(telefono);
  }
}

// ===============================
// EXPORTAR INSTANCIA SINGLETON
// ===============================

export default new EmpresasAdminService();
